import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchProducts } from "../lib/api";
import ProductIcon from "../components/ProductIcon";
import { useDemo } from "../context/DemoContext";
import Seo from "../components/Seo";
import { PAGE_SEO } from "../lib/seo";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Products() {
  const { openDemo } = useDemo();
  const { t, i18n } = useTranslation();
  const [products, setProducts] = useState([]);
  useEffect(() => { fetchProducts().then(setProducts).catch(() => {}); }, [i18n.language]);

  return (
    <div data-testid="products-page" className="pt-32 md:pt-40">
      <Seo {...PAGE_SEO["/products"]} path="/products" />
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-14 md:pb-16">
        <p className="uppercase tracking-[0.25em] text-xs font-bold text-joyn-orange mb-4">{t("productsPage.eyebrow")}</p>
        <h1 className="font-display font-extrabold text-5xl md:text-6xl tracking-tight leading-[0.98] max-w-4xl">
          {t("productsPage.titleBefore")}<span className="joyn-gradient-text">{t("productsPage.titleBrand")}</span>
        </h1>
        <p className="mt-5 text-lg text-muted-foreground max-w-2xl">{t("productsPage.desc")}</p>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p, i) => (
            <motion.div key={p.slug} custom={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <Link to={`/products/${p.slug}`} data-testid={`product-row-${p.slug}`}
                className="group flex flex-col h-full bg-card border border-border rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-transform">
                <div className="relative h-44 overflow-hidden bg-secondary">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute top-3 left-3 inline-flex h-10 w-10 items-center justify-center rounded-xl text-white shadow" style={{ background: p.accent }}><ProductIcon name={p.icon} size={20} /></span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <p className="uppercase tracking-[0.16em] text-[11px] font-bold mb-2" style={{ color: p.accent }}>{p.category}</p>
                  <h2 className="font-display font-bold text-xl tracking-tight mb-2">{p.name}</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1">{p.description}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold group-hover:text-joyn-orange transition-colors">{t("common.learnMore")} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-24">
        <div className="rounded-3xl overflow-hidden bg-foreground text-white p-10 md:p-16 text-center relative">
          <div className="h-1.5 joyn-gradient-bar absolute top-0 inset-x-0" />
          <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mb-4">{t("productsPage.ctaTitle")}</h2>
          <p className="text-white/70 max-w-xl mx-auto mb-8">{t("productsPage.ctaDesc")}</p>
          <button data-testid="products-cta-demo" onClick={openDemo} className="inline-flex items-center gap-2 bg-white text-foreground font-semibold px-8 py-4 rounded-full hover:-translate-y-1 transition-transform">{t("common.talk")} <ArrowRight size={18} /></button>
        </div>
      </section>
    </div>
  );
}
